import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useCanvasStore } from './canvas-store';
import type { CanvasImage } from './canvas-types';

type Result = {id?:string;status:'idle'|'submitting'|'pending'|'completed'|'failed'|'unknown';error?:string;image?:CanvasImage};
async function readResult(response: Response): Promise<Result> {
  const result=await response.json();
  if(!response.ok)throw new Error(result.error || 'Could not check regeneration. Please retry.');
  return result;
}
const active=(result?:Result)=>result?.status==='submitting'||result?.status==='pending';

export function useImageRegeneration(projectId: number | null, imageId: string, enabled: boolean) {
  const queryClient=useQueryClient();
  const {toast}=useToast();
  const [posting,setPosting]=useState(false);
  const postingRef=useRef(false);
  const requestId=useRef<string | undefined>(undefined);
  const lastStatus=useRef<string | undefined>(undefined);
  const path=`/api/canvas/projects/${projectId}/images/${encodeURIComponent(imageId)}/regenerate`;
  const query=useQuery<Result>({
    queryKey:[path],enabled:enabled&&projectId!=null,
    queryFn:async()=>readResult(await fetch(path,{credentials:'include'})),
    refetchInterval: q=>active(q.state.data) ? 2500 : false,
    refetchOnMount:'always',retry:2,
  });
  useEffect(()=>{
    const result=query.data;
    if(!result || projectId==null)return;
    if(result.image) {
      const image=result.image;
      // Add this persisted result without replacing unsaved canvas edits.
      useCanvasStore.getState()._writeImages(projectId,images=>images.some(i=>i.id===image.id)?images:[...images,image]);
    }
    if(result.error && ['submitting','pending'].includes(lastStatus.current ?? '')) {
      toast({title:'Regeneration needs attention',description:result.error,variant:'destructive'});
    }
    lastStatus.current=result.status;
  },[query.data,projectId,toast]);

  const regenerate=useCallback(async()=>{
    if(postingRef.current || active(query.data) || projectId==null)return;
    postingRef.current=true;setPosting(true);
    requestId.current ??= crypto.randomUUID();
    try {
      const response=await fetch(path,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({requestId:requestId.current})});
      // A lost response keeps the same id; retry resumes accepted work.
      if(response.status>=400 && response.status<500)requestId.current=undefined;
      const result=await readResult(response);
      queryClient.setQueryData([path],result);
      requestId.current=undefined;
      if(result.error)toast({title:'Could not regenerate',description:result.error,variant:'destructive'});
      await queryClient.invalidateQueries({queryKey:[path]});
    } catch(error) {
      toast({title:'Could not regenerate',description:error instanceof Error?error.message:'Please retry.',variant:'destructive'});
      await queryClient.invalidateQueries({queryKey:[path]});
    } finally {postingRef.current=false;setPosting(false);}
  },[path,projectId,query.data,queryClient,toast]);
  return {regenerate,busy:posting||active(query.data)};
}
